import React, { useState } from "react";
import { ChevronDown, ChevronRight, Trash2, Plus, X } from "lucide-react";
import RowReorderControls from "components/Workflows/RowReorderControls";
import VisibleWhenEditor from "components/Workflows/VisibleWhenEditor";
import {
  INTAKE_FIELD_TYPES,
  INTAKE_FIELD_TYPES_RESERVED,
  INTAKE_TYPE_LABELS,
  OPTION_TYPES,
} from "constants/ticketTemplates";

/**
 * IntakeFieldRow (SOUND-506) — one intake field. Inline: key / label / type /
 * required, an expander for type-specific config (options editor for selects,
 * `visibleWhen` predicate, help text / placeholder / column span), reorder, and
 * delete. `key` auto-slugs from the label when left blank.
 */
export default function IntakeFieldRow({
  field,
  index,
  total,
  earlierFields = [],
  onChange,
  onMove,
  onDelete,
  keyError,
}) {
  const [open, setOpen] = useState(false);
  const isOptionType = OPTION_TYPES.includes(field.type);
  const options = field.options || [];

  const setOption = (i, v) => onChange({ options: options.map((o, oi) => (oi === i ? v : o)) });
  const addOption = () => onChange({ options: [...options, ""] });
  const removeOption = (i) => onChange({ options: options.filter((_, oi) => oi !== i) });

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-start gap-2 p-2.5">
        <RowReorderControls
          onUp={() => onMove(-1)}
          onDown={() => onMove(1)}
          isFirst={index === 0}
          isLast={index === total - 1}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <input
              className="input h-9 min-w-[160px] flex-1 text-sm"
              placeholder="Question label"
              value={field.label || ""}
              onChange={(e) => onChange({ label: e.target.value })}
            />
            <select
              className="select h-9 w-auto text-sm"
              value={field.type || "text"}
              onChange={(e) => onChange({ type: e.target.value })}
            >
              {INTAKE_FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {INTAKE_TYPE_LABELS[t]}
                </option>
              ))}
              {INTAKE_FIELD_TYPES_RESERVED.map((t) => (
                <option key={t} value={t} disabled>
                  {INTAKE_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={!!field.required}
                onChange={(e) => onChange({ required: e.target.checked })}
                className="h-4 w-4 rounded border-border text-brand focus:ring-brand/40"
              />
              Required
            </label>
          </div>
          {field.key && (
            <div className="mt-1 font-mono text-[11px] text-muted-foreground">key: {field.key}</div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="btn btn-ghost h-8 w-8 shrink-0 p-0"
          aria-label="Field options"
          aria-expanded={open}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="btn btn-ghost h-8 w-8 shrink-0 p-0 text-destructive"
          aria-label="Delete field"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {keyError && (
        <div className="px-3 pb-2 text-xs text-destructive">{keyError}</div>
      )}

      {open && (
        <div className="space-y-4 border-t border-border bg-muted/20 px-3 py-3">
          {/* Options editor (select / select-other) */}
          {isOptionType && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Options
              </div>
              {options.length === 0 && (
                <p className="text-xs text-muted-foreground">Add at least one option.</p>
              )}
              {options.map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input
                    className="input h-9 flex-1 text-sm"
                    placeholder={`Option ${oi + 1}`}
                    value={opt}
                    onChange={(e) => setOption(oi, e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(oi)}
                    className="btn btn-ghost h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    aria-label="Remove option"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addOption} className="btn btn-outline h-8 text-xs">
                <Plus className="h-3.5 w-3.5" />
                Add option
              </button>
              {field.type === "select-other" && (
                <label className="flex flex-col text-sm">
                  <span className="mb-1 text-xs font-medium text-foreground">
                    Free-text key (for the "Other" answer)
                  </span>
                  <input
                    className="input h-9 w-48 font-mono text-xs"
                    placeholder={`${field.key || "field"}Other`}
                    value={field.otherKey || ""}
                    onChange={(e) => onChange({ otherKey: e.target.value })}
                  />
                </label>
              )}
            </div>
          )}

          {/* visibleWhen */}
          <VisibleWhenEditor
            earlierFields={earlierFields}
            value={field.visibleWhen}
            onChange={(vw) => onChange({ visibleWhen: vw })}
          />

          {/* meta */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-xs font-medium text-foreground">Help text</span>
              <input
                className="input h-9 text-sm"
                placeholder="Shown under the field"
                value={field.helpText || ""}
                onChange={(e) => onChange({ helpText: e.target.value })}
              />
            </label>
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-xs font-medium text-foreground">Placeholder</span>
              <input
                className="input h-9 text-sm"
                value={field.placeholder || ""}
                onChange={(e) => onChange({ placeholder: e.target.value })}
              />
            </label>
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-xs font-medium text-foreground">Width</span>
              <select
                className="select h-9 text-sm"
                value={field.gridCols || 1}
                onChange={(e) => onChange({ gridCols: Number(e.target.value) })}
              >
                <option value={1}>Full width</option>
                <option value={2}>Half width</option>
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
